import type { ToastVariant, Toast, ToastSeverity } from '../context/toastContextCore'
import { getNlsString } from '../nls/nlsRuntime'
import type { NlsKey } from '../nls/strings_nls'
import { collectBlockingWork, type SwitchGuard } from './workspaceSwitch/switchGuards'
import { openWorkspaceTarget } from './workspaceSwitch/openWorkspaceTarget'

export type WorkspaceSwitchSource =
  | 'USER'
  | 'URL'
  | 'NOTIFICATION'
  | 'CREATED'
  | 'JOINED'
  | 'MEMBERSHIP_ENDED'
  | 'SYSTEM'
  | 'UNDO'
  | 'DEEPLINK'
  | 'DRAWER'
  | 'PUSH'
  | 'WIDGET'
  | 'GHOST_ECHO'
  | 'INVITE'
  | 'ACCESS_LOST'
  | 'SWITCHER'

export type WorkspaceSwitchResult = 'SWITCHED' | 'CANCELLED' | 'NOOP' | 'NOT_A_MEMBER'

export interface RequestWorkspaceSwitchOptions {
  toWorkspaceId: string | null
  source: WorkspaceSwitchSource
  landing?: string | null
  beforeCommit?: () => void
  restoreOnStay?: () => void
  options?: SwitchWorkspaceTargetOptions
}

export type SwitchGuardPromptAction = 'SAVE' | 'DISCARD' | 'STAY'

export type SwitchGuardPromptActionResult = { ok: boolean; error?: string }

export interface SwitchGuardPromptRequest {
  fromWorkspaceId: string | null
  toWorkspaceId: string | null
  blocking: SwitchGuard[]
  resolve: (action: SwitchGuardPromptAction) => Promise<SwitchGuardPromptActionResult> | void
}

export type WorkspaceCommitter = (workspaceId: string | null) => Promise<void> | void

export interface SwitchWorkspaceTargetOptions {
  activeWorkspaceId?: string | null
  getActiveWorkspaceId?: () => string | null
  workspaces?: Array<{ id: string; name?: string; slug?: string; isPreview?: boolean }>
  getWorkspaces?: () => Array<{ id: string; name?: string; slug?: string; isPreview?: boolean }>
  workspaceName?: string | null
  switchWorkspace?: (targetId: string) => Promise<boolean | void> | boolean | void
  showToast?: (
    message: string,
    variant?: ToastVariant,
    action?: Toast['action'],
    severity?: ToastSeverity,
    options?: Pick<Toast, 'durationMs'>,
  ) => string
  t?: (key: NlsKey, values?: Record<string, string | number>) => string
  legacyAccessLostToast?: boolean
}

let configuredAdapter: SwitchWorkspaceTargetOptions = {}
let registeredCommitter: WorkspaceCommitter | null = null
let activeWorkspaceGetter: (() => string | null) | null = null
let workspacesGetter: (() => Array<{ id: string; name?: string; slug?: string; isPreview?: boolean }>) | null = null
let sheetPromptHandler: ((request: SwitchGuardPromptRequest) => void) | null = null
let lastSwitch: { fromWorkspaceId: string | null; fromRoute: string } | null = null
let internalActiveWorkspaceId: string | null = null

export const configureWorkspaceSwitchService = (adapter: SwitchWorkspaceTargetOptions): void => {
  configuredAdapter = { ...configuredAdapter, ...adapter }
}

export const registerWorkspaceCommitter = (committer: WorkspaceCommitter): (() => void) => {
  registeredCommitter = committer
  return () => {
    if (registeredCommitter === committer) {
      registeredCommitter = null
    }
  }
}

export const registerWorkspaceProviderBridge = (bridge: {
  getActiveWorkspaceId?: () => string | null
  getWorkspaces?: () => Array<{ id: string; name?: string; slug?: string; isPreview?: boolean }>
}): (() => void) => {
  if (bridge.getActiveWorkspaceId) activeWorkspaceGetter = bridge.getActiveWorkspaceId
  if (bridge.getWorkspaces) workspacesGetter = bridge.getWorkspaces
  return () => {
    activeWorkspaceGetter = null
    workspacesGetter = null
  }
}

export const registerSwitchGuardSheetPrompt = (
  handler: (request: SwitchGuardPromptRequest) => void,
): (() => void) => {
  sheetPromptHandler = handler
  return () => {
    if (sheetPromptHandler === handler) {
      sheetPromptHandler = null
    }
  }
}

export const getLastWorkspaceSwitch = (): { fromWorkspaceId: string | null; fromRoute: string } | null => {
  return lastSwitch
}

export const resetWorkspaceSwitchService = (): void => {
  configuredAdapter = {}
  registeredCommitter = null
  activeWorkspaceGetter = null
  workspacesGetter = null
  sheetPromptHandler = null
  lastSwitch = null
  internalActiveWorkspaceId = null
}

const translateHelper = (
  t: ((key: NlsKey, values?: Record<string, string | number>) => string) | undefined,
  key: NlsKey,
  fallback: string,
  values?: Record<string, string | number>,
): string => {
  if (t) {
    try {
      return t(key, values)
    } catch {
      // fallback
    }
  }
  try {
    return getNlsString(key, values)
  } catch {
    let res = fallback
    if (values) {
      for (const [k, v] of Object.entries(values)) {
        res = res.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v))
      }
    }
    return res
  }
}

const getAvailableWorkspaces = (
  options?: SwitchWorkspaceTargetOptions,
): Array<{ id: string; name?: string; slug?: string; isPreview?: boolean }> => {
  return workspacesGetter
    ? workspacesGetter()
    : options?.getWorkspaces
      ? options.getWorkspaces()
      : options?.workspaces !== undefined
        ? options.workspaces
        : configuredAdapter.getWorkspaces
          ? configuredAdapter.getWorkspaces()
          : (configuredAdapter.workspaces ?? [])
}

/**
 * Universal workspace switch service (WS4, WS5, S2).
 * Handles blocking unsaved work sheets, membership validation, committer callbacks,
 * lastSwitch tracking, and workspace:switched events.
 */
export const requestWorkspaceSwitch = async ({
  toWorkspaceId,
  source,
  landing,
  beforeCommit,
  restoreOnStay,
  options,
}: RequestWorkspaceSwitchOptions): Promise<WorkspaceSwitchResult> => {
  const toId = toWorkspaceId === '' ? null : toWorkspaceId
  const rawCurrentId = activeWorkspaceGetter
    ? activeWorkspaceGetter()
    : options?.getActiveWorkspaceId
      ? options.getActiveWorkspaceId()
      : internalActiveWorkspaceId !== null
        ? internalActiveWorkspaceId
        : options?.activeWorkspaceId !== undefined
          ? options.activeWorkspaceId
          : configuredAdapter.getActiveWorkspaceId
            ? configuredAdapter.getActiveWorkspaceId()
            : (configuredAdapter.activeWorkspaceId ?? null)
  const currentId = rawCurrentId === '' ? null : rawCurrentId

  if (toId === currentId) {
    return 'NOOP'
  }

  // Membership validation
  if (toId !== null && source !== 'CREATED' && source !== 'JOINED') {
    const list = getAvailableWorkspaces(options)
    // If list is known and target is not in it, reject
    if (list.length > 0 && !list.some((w) => w.id === toId)) {
      return 'NOT_A_MEMBER'
    }
  }

  const effectiveShowToast = options?.showToast ?? configuredAdapter.showToast
  const effectiveT = options?.t ?? configuredAdapter.t

  const performCommit = async () => {
    beforeCommit?.()
    internalActiveWorkspaceId = toId
    if (registeredCommitter) {
      await registeredCommitter(toId)
    } else if (options?.switchWorkspace && toId !== null) {
      await options.switchWorkspace(toId)
    } else if (configuredAdapter.switchWorkspace && toId !== null) {
      await configuredAdapter.switchWorkspace(toId)
    }

    const fromRoute =
      typeof window !== 'undefined'
        ? window.location.pathname + window.location.search
        : ''
    lastSwitch = { fromWorkspaceId: currentId, fromRoute }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('workspace:switched', {
          detail: {
            from: currentId,
            to: toId,
            source,
          },
        }),
      )

      if (landing && window.location.pathname !== landing) {
        window.history.pushState(null, '', landing)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
    }
  }

  // SYSTEM switches run no guards and show no sheet (start-up picks, auto-moves)
  if (source === 'SYSTEM') {
    await performCommit()
    return 'SWITCHED'
  }

  const blocking = collectBlockingWork(currentId ?? '', toId ?? '')

  // MEMBERSHIP_ENDED discards all unsaved work without a sheet, then toasts what was discarded
  if (source === 'MEMBERSHIP_ENDED') {
    if (blocking.length > 0) {
      const discardedItems: string[] = []
      for (const guard of blocking) {
        try {
          const desc = guard.describe(currentId ?? '', toId ?? '')
          if (desc) discardedItems.push(desc)
          await guard.discard(currentId ?? '', toId ?? '')
        } catch (error) {
          console.error(`[SwitchService] Error discarding guard ${guard.id}:`, error)
        }
      }
      if (effectiveShowToast && discardedItems.length > 0) {
        const msg = translateHelper(
          effectiveT,
          'NLSWorkspaceSwitchDiscardedAfterLeave',
          'Discarded {items}',
          { items: discardedItems.join(', ') },
        )
        effectiveShowToast(msg, 'info')
      }
    }
    await performCommit()
    return 'SWITCHED'
  }

  // No blocking work: commit immediately
  if (blocking.length === 0) {
    await performCommit()
    return 'SWITCHED'
  }

  // With blocking work otherwise: prompt user via the sheet
  if (!sheetPromptHandler) {
    console.warn(
      `[WorkspaceSwitchService] Blocking work exists but no switch guard sheet is mounted. Guards: ${blocking.map((g) => g.id).join(', ')}`,
    )
    return 'CANCELLED'
  }

  return await new Promise<WorkspaceSwitchResult>((resolveSwitch) => {
    sheetPromptHandler!({
      fromWorkspaceId: currentId,
      toWorkspaceId: toId,
      blocking,
      resolve: async (action: SwitchGuardPromptAction) => {
        if (action === 'STAY') {
          restoreOnStay?.()
          resolveSwitch('CANCELLED')
          return { ok: false }
        }

        if (action === 'DISCARD') {
          for (const guard of blocking) {
            try {
              await guard.discard(currentId ?? '', toId ?? '')
            } catch (error) {
              console.error(`[SwitchService] Error discarding guard ${guard.id}:`, error)
            }
          }
          await performCommit()
          resolveSwitch('SWITCHED')
          return { ok: true }
        }

        if (action === 'SAVE') {
          for (const guard of blocking) {
            if (guard.save) {
              try {
                await guard.save(currentId ?? '', toId ?? '')
              } catch (error) {
                const message = error instanceof Error ? error.message : 'Save failed'
                if (effectiveShowToast) {
                  effectiveShowToast(message, 'error')
                }
                return { ok: false, error: message }
              }
            }
          }
          // H5: after all saves succeed, run discard on guards without save
          for (const guard of blocking) {
            if (!guard.save) {
              try {
                await guard.discard(currentId ?? '', toId ?? '')
              } catch (error) {
                console.error(`[SwitchService] Error discarding non-saveable guard ${guard.id}:`, error)
              }
            }
          }
          await performCommit()
          resolveSwitch('SWITCHED')
          return { ok: true }
        }

        resolveSwitch('CANCELLED')
        return { ok: false }
      },
    })
  })
}

/**
 * Shared workspace switch-for-target service (TM24, WS4, R9).
 * Thin wrapper that delegates to openWorkspaceTarget (single implementation).
 */
export const switchToWorkspaceForTarget = async (
  workspaceId: string | null | undefined,
  open?: (() => void | Promise<void>) | null,
  options?: SwitchWorkspaceTargetOptions,
): Promise<boolean> => {
  const merged: SwitchWorkspaceTargetOptions = {
    ...configuredAdapter,
    ...options,
    legacyAccessLostToast: true,
  }
  return openWorkspaceTarget({
    workspaceId,
    open,
    source: 'NOTIFICATION',
    options: merged,
  })
}
